import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";export const SUPPORTED_LANGS = ["en", "de", "fr", "pt"];

/**
 * Returns a function that prefixes a path with the current URL language segment, if any.
 * - localePath("/#contact") -> "/de/#contact" when URL is /de
 * - localePath("/products/lite") -> "/de/products/lite" when URL is /de
 * - When no lang prefix is present in URL, paths stay as-is.
 */
export function useLocalePath() {
  const params = useParams();
  const lang = params.lang && SUPPORTED_LANGS.includes(params.lang) ? params.lang : null;
  return React.useCallback(
    (path = "/") => {
      if (!lang) return path;
      if (path.startsWith("#")) return `/${lang}${path}`;
      // anchor link like "/#contact" => "/<lang>/#contact"
      if (path === "/") return `/${lang}`;
      if (path.startsWith("/")) return `/${lang}${path}`;
      return path;
    },
    [lang]
  );
}

/**
 * Syncs the URL language prefix (/de, /en, /fr, /pt) with the i18n instance.
 * - If a valid lang is present in the URL, force i18n to use it.
 * - If the URL has no lang prefix, do nothing (i18n keeps its detected/stored language).
 * - If the URL has an invalid lang prefix, strip it and redirect to root.
 */
export function useLangSync() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!lang) return;
    if (!SUPPORTED_LANGS.includes(lang)) {
      // Strip invalid lang segment from path
      const stripped = location.pathname.replace(`/${lang}`, "") || "/";
      navigate(stripped + location.search + location.hash, { replace: true });
      return;
    }
    const current = i18n.language?.split("-")[0];
    if (current !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, navigate, location.pathname, location.search, location.hash]);
}

/**
 * Build a localised URL preserving the current relative path.
 * If targetLang === default behaviour, we keep the lang prefix explicit for SEO.
 */
export function buildLangUrl(targetLang, currentPathname) {
  const parts = currentPathname.split("/").filter(Boolean);
  if (parts.length > 0 && SUPPORTED_LANGS.includes(parts[0])) {
    parts.shift();
  }
  const rest = parts.length ? "/" + parts.join("/") : "";
  return `/${targetLang}${rest}`;
}
